import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// ============================================================
// Extraction Trainer — teach the company-extraction judge by example.
//
// Flow: pick/create a ruleset (keywords + what "relevant" means) → load a
// stratified sample of profiles the keyword net caught → highlight evidence
// text and POINT IT AT A COMPANY with a note + verdict (the training
// examples) → preview-judge a sample and correct any wrong verdicts
// (corrections become examples too) → full run creates companies tagged
// Include / Review.
//
// Sample profiles mirror the candidate side panel (header, summary, skills,
// experience timeline) with net-keyword hits pre-highlighted, so training
// reads like scanning a real profile, not raw text blocks.
// ============================================================

type Ruleset = { id: number; name: string; task_description: string; keywords: string[]; example_count?: number };
type Highlight = { field: string; text: string; note: string };
type Snapshot = { name: string; headline: string | null; skills: string | null; summary: string | null; positions: Array<{ title: string | null; companyName: string | null; description: string | null; years: string | null }> };
type Unit = {
  candidateId: number; candidateName: string; companyName: string; companyUrl?: string | null;
  bucket: string; evidence: Array<{ field: string; keyword: string; snippet: string }>;
  snapshot: Snapshot;
};
type Judged = Unit & { verdict: string; reasoning: string; citedEvidence: string[] };
type Profile = { candidateId: number; candidateName: string; snapshot: Snapshot; units: Unit[] };

const BUCKET_LABELS: Record<string, string> = {
  skills_only: 'Skills-only signal', headline: 'Headline signal', summary_only: 'Summary signal',
  past_role: 'Past role', current_role: 'Current role', other: 'Other',
};

const ExtractionTrainer: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const auth = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Rulesets
  const [rulesets, setRulesets] = useState<Ruleset[]>([]);
  const [rulesetId, setRulesetId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [keywordsText, setKeywordsText] = useState('');
  const [saving, setSaving] = useState(false);

  // Training
  const [examples, setExamples] = useState<any[]>([]);
  const [sample, setSample] = useState<Unit[]>([]);
  const [netStats, setNetStats] = useState<any>(null);
  const [loadingSample, setLoadingSample] = useState(false);
  // Active selection: highlighted text waiting to be pointed at a company
  const [sel, setSel] = useState<{ candidateId: number; field: string; text: string; company: string; note: string } | null>(null);

  // Preview
  const [previewing, setPreviewing] = useState(false);
  const [previewResults, setPreviewResults] = useState<Judged[] | null>(null);
  const [previewTotalUnits, setPreviewTotalUnits] = useState(0);
  const [correcting, setCorrecting] = useState<number | null>(null);
  const [correctionNote, setCorrectionNote] = useState('');

  // Full run
  const [running, setRunning] = useState(false);
  const [runStats, setRunStats] = useState<any>(null);
  const [runOpId, setRunOpId] = useState<string | null>(null);
  const pollRef = useRef<any>(null);

  const ruleset = rulesets.find(r => r.id === rulesetId) || null;

  // Tolerant keyword parsing: users paste boolean search strings
  // ('"commercial roofing" AND ("roofer" OR "EPDM")'), and literal quote marks
  // silently starved the net (searching for text CONTAINING the quotes caught
  // 3 profiles out of 33k). Split on commas AND boolean connectors, strip
  // quotes/parens, dedupe.
  const parseKeywords = (text: string): string[] => {
    const parts = text
      .replace(/[()]/g, ' ')
      .split(/,|\bOR\b|\bAND\b|\bNOT\b/i)
      .map(k => k.trim().replace(/^["']+|["']+$/g, '').trim())
      .filter(k => k.length > 1);
    const out: string[] = [];
    parts.forEach(k => {
      const lk = k.toLowerCase();
      let seen = false;
      for (let i = 0; i < out.length; i++) { if (out[i].toLowerCase() === lk) { seen = true; break; } }
      if (!seen) out.push(k);
    });
    return out;
  };
  const keywords = parseKeywords(keywordsText);
  // Continuous training: profiles are paged from the net one batch at a time
  // and shown one profile per screen — the user advances until THEY are happy
  // with the ruleset; the flow never stops them at a fixed sample size.
  const BATCH = 10;
  const [cursor, setCursor] = useState(0);

  // Regex used to pre-highlight net keyword hits in profile text. Split with a
  // capture group alternates plain/match parts (odd indices = matches).
  const keywordRegex = useMemo(() => {
    if (!keywords.length) return null;
    const esc = keywords.map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    return new RegExp(`(${esc.join('|')})`, 'gi');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywordsText]);

  const mark = (text: string | null | undefined): React.ReactNode => {
    if (!text) return null;
    if (!keywordRegex) return text;
    const parts = String(text).split(keywordRegex);
    return parts.map((p, i) => i % 2 === 1
      ? <mark key={i} className="bg-yellow-200 rounded px-0.5">{p}</mark>
      : p);
  };

  // One card per candidate (a candidate can implicate several companies).
  const profiles: Profile[] = useMemo(() => {
    const map: Record<string, Profile> = {};
    const orderedKeys: string[] = [];
    sample.forEach(u => {
      const k = String(u.candidateId);
      if (!map[k]) { map[k] = { candidateId: u.candidateId, candidateName: u.candidateName, snapshot: u.snapshot, units: [] }; orderedKeys.push(k); }
      map[k].units.push(u);
    });
    return orderedKeys.map(k => map[k]);
  }, [sample]);

  const loadRulesets = useCallback(async () => {
    const res = await fetch('/api/extraction/rulesets', { headers: auth });
    if (res.ok) setRulesets(await res.json());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadRulesets(); }, [loadRulesets]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  useEffect(() => {
    if (!ruleset) return;
    setName(ruleset.name);
    setTaskDescription(ruleset.task_description || '');
    setKeywordsText((Array.isArray(ruleset.keywords) ? ruleset.keywords : []).join(', '));
    (async () => {
      const res = await fetch(`/api/extraction/rulesets/${ruleset.id}/examples`, { headers: auth });
      if (res.ok) setExamples(await res.json());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesetId]);

  const saveRuleset = async () => {
    if (!name.trim()) { alert('Give the ruleset a name'); return; }
    setSaving(true);
    try {
      const body = JSON.stringify({ name: name.trim(), taskDescription, keywords });
      const res = rulesetId
        ? await fetch(`/api/extraction/rulesets/${rulesetId}`, { method: 'PUT', headers: auth, body })
        : await fetch('/api/extraction/rulesets', { method: 'POST', headers: auth, body });
      if (res.ok) {
        const rs = await res.json();
        setRulesetId(rs.id);
        await loadRulesets();
      }
    } finally { setSaving(false); }
  };

  const loadSample = async () => {
    if (!keywords.length) { alert('Add at least one keyword first'); return; }
    setLoadingSample(true);
    setSample([]);
    setCursor(0);
    setSel(null);
    try {
      const res = await fetch(`/api/extraction/net/${projectId}`, {
        method: 'POST', headers: auth, body: JSON.stringify({ keywords, sampleSize: BATCH, offset: 0 }),
      });
      if (res.ok) {
        const data = await res.json();
        setSample(data.sample || []);
        setNetStats(data);
      }
    } finally { setLoadingSample(false); }
  };

  // Pull the next page of net signals and append (continuous training).
  const loadMore = async (): Promise<number> => {
    if (!netStats || !netStats.hasMore) return 0;
    const res = await fetch(`/api/extraction/net/${projectId}`, {
      method: 'POST', headers: auth, body: JSON.stringify({ keywords, sampleSize: BATCH, offset: netStats.nextOffset }),
    });
    if (!res.ok) return 0;
    const data = await res.json();
    setSample(prev => prev.concat(data.sample || []));
    setNetStats(data);
    return (data.sample || []).length;
  };

  const nextProfile = async () => {
    setSel(null);
    if (cursor + 1 < profiles.length) { setCursor(cursor + 1); return; }
    if (netStats && netStats.hasMore) {
      setLoadingSample(true);
      try {
        const added = await loadMore();
        if (added > 0) setCursor(c => c + 1);
      } finally { setLoadingSample(false); }
    }
  };

  const prevProfile = () => {
    setSel(null);
    setCursor(c => Math.max(0, c - 1));
  };

  // Companies this profile could point at: every employer in the timeline
  // plus any net-implicated companies.
  const companiesFor = (profile: Profile): string[] => {
    const opts: string[] = [];
    (profile.snapshot.positions || []).forEach(p => { if (p.companyName && opts.indexOf(p.companyName) < 0) opts.push(p.companyName); });
    profile.units.forEach(u => { if (u.companyName && opts.indexOf(u.companyName) < 0) opts.push(u.companyName); });
    return opts;
  };

  // Selection anywhere on a profile card: find which labelled field it was in
  // and pre-guess the company (the role's employer for a role highlight,
  // otherwise the current employer).
  const onCardMouseUp = (profile: Profile) => {
    const selection = window.getSelection();
    const text = selection ? selection.toString().trim() : '';
    if (!text || text.length < 2) return;
    let node: any = selection!.anchorNode;
    while (node && node.nodeType !== 1) node = node.parentNode;
    const fieldEl = node ? (node as Element).closest('[data-field]') : null;
    const field = fieldEl ? (fieldEl.getAttribute('data-field') || 'profile') : 'profile';
    let company = '';
    const m = field.match(/^position\[(\d+)\]/);
    if (m) company = ((profile.snapshot.positions || [])[parseInt(m[1], 10)] || {}).companyName || '';
    if (!company) company = ((profile.snapshot.positions || [])[0] || {}).companyName || (profile.units[0] ? profile.units[0].companyName : '');
    setSel({ candidateId: profile.candidateId, field, text, company, note: '' });
  };

  const saveExample = async (args: { candidateId: number; companyName: string; snapshot: Snapshot; highlights: Highlight[]; verdict: string; note: string; source: 'manual' | 'correction' }) => {
    if (!rulesetId) { alert('Save the ruleset first'); return; }
    const res = await fetch(`/api/extraction/rulesets/${rulesetId}/examples`, {
      method: 'POST', headers: auth,
      body: JSON.stringify({
        candidateId: args.candidateId,
        companyName: args.companyName,
        candidateSnapshot: args.snapshot,
        highlights: args.highlights,
        verdict: args.verdict,
        note: args.note,
        source: args.source,
      }),
    });
    if (res.ok) {
      const ex = await res.json();
      setExamples(prev => [ex, ...prev]);
    }
  };

  // Highlight → company → verdict, saved as one example.
  const savePointedExample = async (profile: Profile, verdict: string) => {
    if (!sel) return;
    await saveExample({
      candidateId: profile.candidateId,
      companyName: sel.company,
      snapshot: profile.snapshot,
      highlights: [{ field: sel.field, text: sel.text, note: sel.note }],
      verdict,
      note: sel.note,
      source: 'manual',
    });
    setSel(null);
  };

  const runPreview = async () => {
    if (!rulesetId) { alert('Save the ruleset first'); return; }
    setPreviewing(true);
    setPreviewResults(null);
    try {
      const res = await fetch(`/api/extraction/preview/${projectId}`, {
        method: 'POST', headers: auth, body: JSON.stringify({ rulesetId, sampleSize: 15 }),
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewResults(data.judged || []);
        setPreviewTotalUnits(data.totalUnits || 0);
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Preview failed');
      }
    } finally { setPreviewing(false); }
  };

  const correctVerdict = async (idx: number, newVerdict: string) => {
    if (!previewResults) return;
    const r = previewResults[idx];
    await saveExample({
      candidateId: r.candidateId,
      companyName: r.companyName,
      snapshot: r.snapshot,
      highlights: r.evidence.map(e => ({ field: e.field, text: e.snippet, note: `matched "${e.keyword}"` })),
      verdict: newVerdict,
      note: correctionNote || `Corrected from ${r.verdict} to ${newVerdict}.`,
      source: 'correction',
    });
    const updated = previewResults.slice();
    updated[idx] = { ...r, verdict: newVerdict, reasoning: `(corrected by you) ${correctionNote || ''}` };
    setPreviewResults(updated);
    setCorrecting(null);
    setCorrectionNote('');
  };

  const startRun = async () => {
    if (!rulesetId) { alert('Save the ruleset first'); return; }
    setRunning(true);
    setRunStats(null);
    try {
      // First call returns the real scale (companies to judge) so the user
      // confirms with numbers in front of them — never a blind spend.
      let res = await fetch(`/api/extraction/run/${projectId}`, {
        method: 'POST', headers: auth, body: JSON.stringify({ rulesetId }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); alert(err.error || 'Failed to start run'); setRunning(false); return; }
      let data = await res.json();
      if (data.needsConfirmation) {
        const ok = window.confirm(
          `The net caught ${data.totalUnits} candidate signals across ${data.totalCompanies} unique companies (from ${data.candidatesScanned} candidates).\n\n` +
          `Judging will make ~${data.estimatedCalls} AI calls (companies are judged in batches). Companies judged Include or Review will be created in this project.\n\nRun it?`
        );
        if (!ok) { setRunning(false); return; }
        res = await fetch(`/api/extraction/run/${projectId}`, {
          method: 'POST', headers: auth, body: JSON.stringify({ rulesetId, confirm: true }),
        });
        if (!res.ok) { const err = await res.json().catch(() => ({})); alert(err.error || 'Failed to start run'); setRunning(false); return; }
        data = await res.json();
      }
      const operationId = data.operationId;
      setRunOpId(operationId);
      pollRef.current = setInterval(async () => {
        const pr = await fetch(`/api/extraction/run-progress/${operationId}`, { headers: auth });
        if (pr.status === 404) {
          // Server restarted mid-run — the in-memory progress is gone.
          clearInterval(pollRef.current);
          setRunStats({ status: 'failed', error: 'Run was interrupted (server restarted). Start it again.' });
          setRunning(false);
          return;
        }
        if (pr.ok) {
          const p = await pr.json();
          setRunStats(p);
          if (p.status === 'complete' || p.status === 'failed' || p.status === 'cancelled') {
            clearInterval(pollRef.current);
            setRunning(false);
          }
        }
      }, 2000);
    } catch {
      setRunning(false);
    }
  };

  const cancelRun = async () => {
    if (!runOpId) return;
    await fetch(`/api/extraction/run-cancel/${runOpId}`, { method: 'POST', headers: auth });
  };

  const verdictChip = (v: string) => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
      v === 'include' ? 'bg-green-100 text-green-800' : v === 'review' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700'
    }`}>{v.toUpperCase()}</span>
  );

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">🎓 Extraction Trainer</h1>
          <p className="text-sm text-gray-600 mt-1">Teach the judge what a relevant company looks like — highlight evidence on a profile, point it at the company it implicates, add your reasoning. When unsure it says <span className="font-medium">Review</span> rather than dropping a company.</p>
        </div>
        <button onClick={() => navigate(`/project/${projectId}`)} className="text-sm text-blue-600 hover:underline">← Back to project</button>
      </div>

      {/* 1 — Ruleset */}
      <div className="card p-5">
        <h2 className="font-semibold mb-3">1 · Ruleset</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Load existing</label>
            <select className="input text-sm" value={rulesetId || ''} onChange={e => setRulesetId(e.target.value ? parseInt(e.target.value) : null)}>
              <option value="">— New ruleset —</option>
              {rulesets.map(r => <option key={r.id} value={r.id}>{r.name} ({r.example_count || 0} examples)</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
            <input className="input text-sm" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Commercial roofing companies" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">What counts as a relevant company? (in your words)</label>
            <textarea className="input text-sm" rows={2} value={taskDescription} onChange={e => setTaskDescription(e.target.value)}
              placeholder="e.g. Companies that install or maintain commercial/industrial roofing. Residential-only firms are out. Distributors of roofing materials are borderline — mark Review." />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Net keywords (comma-separated — cast wide, the judge filters. Quotes, AND/OR and brackets are stripped automatically)</label>
            <input className="input text-sm" value={keywordsText} onChange={e => setKeywordsText(e.target.value)} placeholder="e.g. commercial roofing, flat roof, cladding, building envelope" />
            {keywords.length > 0 && (
              <p className="text-xs text-gray-500 mt-1.5">
                Net will search for:{' '}
                {keywords.map((k, i) => <span key={i} className="inline-block bg-yellow-100 text-gray-800 rounded px-1.5 py-0.5 mr-1 mb-1">{k}</span>)}
              </p>
            )}
          </div>
        </div>
        <button onClick={saveRuleset} disabled={saving} className="btn-primary text-sm mt-3">{saving ? 'Saving…' : rulesetId ? 'Update ruleset' : 'Create ruleset'}</button>
      </div>

      {/* 2 — Train on sample profiles */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">2 · Train, profile by profile <span className="text-xs font-normal text-gray-500">({examples.length} examples saved)</span></h2>
          <button onClick={loadSample} disabled={loadingSample || !keywords.length} className="btn-primary text-sm">
            {loadingSample && !sample.length ? 'Scanning…' : sample.length ? '↺ Restart from first profile' : 'Start training'}
          </button>
        </div>
        {netStats && (
          <p className="text-xs text-gray-600 mb-3">
            Net caught <span className="font-semibold">{netStats.totalUnits}</span> candidate→company signals across <span className="font-semibold">{netStats.uniqueCompanies}</span> companies (from {netStats.candidatesScanned} candidates). Keyword hits are <mark className="bg-yellow-200 rounded px-0.5">highlighted</mark> below. Select any text on a profile to point it at a company.
          </p>
        )}

        {profiles.length > 0 && (
          <div className="flex items-center justify-between mb-3">
            <button onClick={prevProfile} disabled={cursor === 0} className="text-sm text-blue-600 hover:underline disabled:opacity-30 disabled:no-underline">← Previous</button>
            <span className="text-xs text-gray-500">
              Profile {Math.min(cursor, profiles.length - 1) + 1} of {profiles.length}{netStats && netStats.hasMore ? '+' : ''} · annotate what teaches, skip what doesn't — keep going until the ruleset feels right
            </span>
            <button
              onClick={nextProfile}
              disabled={loadingSample || (!(netStats && netStats.hasMore) && cursor >= profiles.length - 1)}
              className="btn-primary text-sm"
            >
              {loadingSample ? 'Loading…' : 'Next profile →'}
            </button>
          </div>
        )}
        <div className="space-y-5">
          {profiles.filter((_, pi) => pi === Math.min(cursor, profiles.length - 1)).map((profile) => {
            const snap = profile.snapshot;
            const cur = (snap.positions || [])[0];
            const companyOpts = companiesFor(profile);
            const selHere = sel && sel.candidateId === profile.candidateId;
            return (
              <div key={profile.candidateId} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* Header — mirrors the candidate side panel */}
                <div className="px-5 py-4 bg-gray-50 border-b border-gray-100 flex items-start gap-3" data-field="headline" onMouseUp={() => onCardMouseUp(profile)}>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-base font-semibold flex-shrink-0">
                    {(profile.candidateName || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900">{profile.candidateName}</h3>
                    {snap.headline && <p className="text-sm text-gray-600">{mark(snap.headline)}</p>}
                    {cur && cur.title && cur.companyName && (
                      <p className="text-sm text-gray-500 mt-0.5">{cur.title} at <span className="font-medium text-gray-700">{cur.companyName}</span></p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    {profile.units.map((u, ui) => (
                      <span key={ui} className="block mb-1 px-2 py-0.5 rounded bg-violet-50 text-violet-700 text-[10px] font-medium" title={`Net signal: ${u.evidence.map(e => `"${e.keyword}" in ${e.field}`).join(', ')}`}>
                        {u.companyName} · {BUCKET_LABELS[u.bucket] || u.bucket}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Selection panel — «text» points to → company */}
                {selHere && (
                  <div className="px-5 py-3 bg-yellow-50 border-b-2 border-yellow-300">
                    <p className="text-sm text-gray-800 mb-2">
                      <mark className="bg-yellow-200 rounded px-1">“{sel!.text.slice(0, 140)}”</mark>
                      <span className="text-gray-500 mx-2">points to →</span>
                      <select
                        className="input text-sm inline-block w-auto py-1"
                        value={sel!.company}
                        onChange={e => setSel({ ...sel!, company: e.target.value })}
                      >
                        {companyOpts.map(c => <option key={c} value={c}>{c}</option>)}
                        {companyOpts.indexOf(sel!.company) < 0 && sel!.company && <option value={sel!.company}>{sel!.company}</option>}
                      </select>
                    </p>
                    <input
                      className="input text-sm mb-2" autoFocus value={sel!.note}
                      onChange={e => setSel({ ...sel!, note: e.target.value })}
                      placeholder="Why? e.g. 'this skill implies the employer does commercial roofing even though the title is generic'"
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">So {sel!.company || 'this company'} is:</span>
                      <button onClick={() => savePointedExample(profile, 'include')} className="px-2.5 py-1 bg-green-600 text-white rounded text-xs font-medium">✓ Include</button>
                      <button onClick={() => savePointedExample(profile, 'review')} className="px-2.5 py-1 bg-amber-500 text-white rounded text-xs font-medium">? Review</button>
                      <button onClick={() => savePointedExample(profile, 'exclude')} className="px-2.5 py-1 bg-gray-600 text-white rounded text-xs font-medium">✕ Exclude</button>
                      <button onClick={() => setSel(null)} className="px-2 py-1 text-xs text-gray-500">Cancel</button>
                    </div>
                  </div>
                )}

                {/* Body — summary, skills, experience timeline */}
                <div className="px-5 py-4" onMouseUp={() => onCardMouseUp(profile)}>
                  {snap.summary && (
                    <div className="mb-3" data-field="summary">
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Summary</p>
                      <p className="text-sm text-gray-700 whitespace-pre-line cursor-text">{mark(snap.summary)}</p>
                    </div>
                  )}
                  {snap.skills && (
                    <div className="mb-3" data-field="skills">
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Skills</p>
                      <p className="text-sm text-gray-700 cursor-text">{mark(snap.skills)}</p>
                    </div>
                  )}
                  {(snap.positions || []).length > 0 && (
                    <div>
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Experience</p>
                      {(snap.positions || []).map((p, pi, arr) => (
                        <div key={pi} className="flex gap-3" data-field={`position[${pi}]`}>
                          <div className="flex flex-col items-center">
                            <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${pi === 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                            {pi < arr.length - 1 && <div className="w-px flex-1 bg-gray-200" />}
                          </div>
                          <div className={pi < arr.length - 1 ? 'pb-4 cursor-text' : 'cursor-text'}>
                            <p className="text-sm font-medium text-gray-900">{mark(p.title || 'Unknown role')}</p>
                            <p className="text-sm text-gray-600">{p.companyName}{p.years ? <span className="text-gray-400"> · {p.years}</span> : null}</p>
                            {p.description && <p className="text-xs text-gray-500 mt-1 whitespace-pre-line">{mark(p.description)}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {!profiles.length && !loadingSample && <p className="text-sm text-gray-500">Hit "Start training" to load the first profile — you'll step through one at a time and stop whenever you're satisfied.</p>}
        </div>

        {examples.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Saved examples</h3>
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {examples.map((e: any) => {
                const firstHl = Array.isArray(e.highlights) && e.highlights.length ? e.highlights[0] : null;
                return (
                  <li key={e.id} className="text-xs text-gray-600 flex items-center justify-between gap-2">
                    <span className="truncate">
                      {verdictChip(e.verdict)}{' '}
                      {firstHl && <mark className="bg-yellow-100 rounded px-1">“{String(firstHl.text).slice(0, 50)}”</mark>}
                      <span className="text-gray-400"> → </span>
                      <span className="font-medium">{e.company_name}</span>
                      {e.note && <span className="text-gray-500"> — {e.note.slice(0, 70)}</span>}
                      {e.source === 'correction' && <span className="ml-1 text-[10px] text-purple-600">(correction)</span>}
                    </span>
                    <button onClick={async () => { await fetch(`/api/extraction/examples/${e.id}`, { method: 'DELETE', headers: auth }); setExamples(examples.filter((x: any) => x.id !== e.id)); }} className="text-red-400 hover:text-red-600">✕</button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* 3 — Preview & correct */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold">3 · Preview the judge <span className="text-xs font-normal text-gray-500">(15 sample judgments — correct anything wrong and the correction trains it)</span></h2>
          <button onClick={runPreview} disabled={previewing || !rulesetId} className="btn-primary text-sm">{previewing ? 'Judging…' : 'Run preview'}</button>
        </div>
        {previewResults && (
          <>
            <p className="text-xs text-gray-600 mb-2">{previewTotalUnits} total signals in the project; showing 15 judged samples.</p>
            <div className="space-y-2 max-h-[480px] overflow-y-auto">
              {previewResults.map((r, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm">
                      {verdictChip(r.verdict)} <span className="font-semibold text-violet-700 ml-1">{r.companyName}</span>
                      <span className="text-gray-500"> via {r.candidateName}</span>
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px]">{BUCKET_LABELS[r.bucket] || r.bucket}</span>
                    </div>
                    {correcting !== idx ? (
                      <button onClick={() => { setCorrecting(idx); setCorrectionNote(''); }} className="text-xs text-blue-600 hover:underline">Correct</button>
                    ) : null}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{r.reasoning}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Evidence: {r.evidence.map(e => `"${e.keyword}" in ${e.field}`).join('; ')}</p>
                  {correcting === idx && (
                    <div className="mt-2 p-2 bg-purple-50 border border-purple-200 rounded">
                      <input className="input text-xs mb-2" autoFocus value={correctionNote} onChange={e => setCorrectionNote(e.target.value)}
                        placeholder="Why is the judge wrong here? This note becomes a training example." />
                      <div className="flex gap-1.5">
                        <button onClick={() => correctVerdict(idx, 'include')} className="px-2 py-0.5 bg-green-600 text-white rounded text-xs">→ Include</button>
                        <button onClick={() => correctVerdict(idx, 'review')} className="px-2 py-0.5 bg-amber-500 text-white rounded text-xs">→ Review</button>
                        <button onClick={() => correctVerdict(idx, 'exclude')} className="px-2 py-0.5 bg-gray-600 text-white rounded text-xs">→ Exclude</button>
                        <button onClick={() => setCorrecting(null)} className="px-2 py-0.5 text-xs text-gray-500">Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* 4 — Full run */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold">4 · Full run</h2>
          <div className="flex items-center gap-2">
            {running && (
              <button onClick={cancelRun} className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm font-medium">
                ✕ Cancel run
              </button>
            )}
            <button onClick={startRun} disabled={running || !rulesetId} className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium disabled:opacity-40">
              {running ? 'Running…' : 'Run on all candidates'}
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mb-2">Judges every signal the net caught, then creates companies in the project. Include and Review companies get an "Extraction Verdict" column so you can filter the Review pile and eyeball it — nothing relevant is silently dropped.</p>
        {runStats && (
          <div className="text-sm text-gray-700">
            {runStats.status === 'judging' && <p>Judging… {runStats.done}/{runStats.total} companies</p>}
            {runStats.status === 'writing' && (
              <p>Judged all {runStats.total} — <span className="text-green-700 font-semibold">{runStats.include} include</span>, <span className="text-amber-700 font-semibold">{runStats.review} review</span>, {runStats.exclude} excluded. Writing companies to the project…</p>
            )}
            {runStats.status === 'complete' && (
              <p>✅ Done — <span className="text-green-700 font-semibold">{runStats.include} include</span>, <span className="text-amber-700 font-semibold">{runStats.review} review</span>, {runStats.exclude} excluded. {runStats.companiesCreated} new companies created.{runStats.writeFailures > 0 && <span className="text-red-600"> ({runStats.writeFailures} failed to write — check server logs)</span>} <button onClick={() => navigate(`/project/${projectId}`)} className="text-blue-600 hover:underline">Open project →</button></p>
            )}
            {runStats.status === 'cancelled' && <p className="text-amber-700">⏹ Cancelled after {runStats.done}/{runStats.total} judgments — no companies were created.</p>}
            {runStats.status === 'failed' && <p className="text-red-600">❌ Run failed: {runStats.error}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtractionTrainer;
